import React, { useState, useEffect } from "react";
import { Navigate, Link } from 'react-router-dom'
import Cookies from 'universal-cookie';
import '../index.css';
import logo from '../assets/spinner.gif'
import { populateCustomerList, UseLocalStorage, GetAttributesByAssignment } from "../functions";

function Customers() {
  const [localStorageAttributes, setLocalStorageAttributes] = UseLocalStorage('attributes', []);
  const [localStorageCustomers, setLocalStorageCustomers] = UseLocalStorage('customers', []);
  const customerAttributes = GetAttributesByAssignment('customers', localStorageAttributes);
  const customerTags = customerAttributes.tags.sort((a, b) => a.displayorder.localeCompare(b.displayorder));

  const [pageTitle, setPageTitle] = UseLocalStorage('pageTitle', 'Customers');
  const [pageAttr, setPageAttr] = UseLocalStorage('pageAttr', 'Customers');

  const [loading, setLoading] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [filteredResults, setFilteredResults] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(100);

  const cookies = new Cookies();
  let login = cookies.get('login');

  useEffect(() => {
    if (login) {
      const loadPost = async () => {
        setLoading(true);
        let customers = await populateCustomerList();
        customers.sort((a, b) => a.name.localeCompare(b.name)); // sort by name
        setLocalStorageCustomers(customers);
        setFilteredResults(customers);

        let attributes = localStorageAttributes;
        const ticketAttributes = GetAttributesByAssignment('customers', attributes);
        setPageTitle(ticketAttributes.displayplural);
        setPageAttr(ticketAttributes.displayname);

        setLoading(false);
      };
      loadPost();
    }
  }, [])

  if (!login) {
    return (<Navigate to={{ pathname: '../Login' }} />);
  }

  const handleSearch = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);
    setCurrentPage(1); // Reset to first page when searching

    // Filter logic - search across multiple fields
    const results = localStorageCustomers.filter(
      (c) =>
        c.name.toLowerCase().includes(query) ||
        (c.email && c.email.toLowerCase().includes(query)) ||
        (c.phone && c.phone.toLowerCase().includes(query)) ||
        (c.address && c.address.toLowerCase().includes(query))
    );
    setFilteredResults(results);
  };

  // Calculate pagination
  const totalPages = Math.ceil(filteredResults.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentResults = filteredResults.slice(startIndex, endIndex);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pageNumbers = [];
    const maxVisiblePages = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // Previous button
    if (currentPage > 1) {
      pageNumbers.push(
        <button key="prev" onClick={() => handlePageChange(currentPage - 1)}>
          Previous
        </button>
      );
    }

    // First page
    if (startPage > 1) {
      pageNumbers.push(
        <button key={1} onClick={() => handlePageChange(1)}>
          1
        </button>
      );
      if (startPage > 2) {
        pageNumbers.push(<span key="ellipsis1">...</span>);
      }
    }

    // Page numbers
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          style={{
            backgroundColor: currentPage === i ? '#444444' : '#333333',
            fontWeight: currentPage === i ? 'bold' : 'normal'
          }}
        >
          {i}
        </button>
      );
    }

    // Last page
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pageNumbers.push(<span key="ellipsis2">...</span>);
      }
      pageNumbers.push(
        <button key={totalPages} onClick={() => handlePageChange(totalPages)}>
          {totalPages}
        </button>
      );
    }

    // Next button
    if (currentPage < totalPages) {
      pageNumbers.push(
        <button key="next" onClick={() => handlePageChange(currentPage + 1)}>
          Next
        </button>
      );
    }

    return (
      <div className="pagination-container">
        <div className="pagination-info">
          Showing {startIndex + 1}-{Math.min(endIndex, filteredResults.length)} of {filteredResults.length} results
        </div>
        <div className="pagination-buttons">
          {pageNumbers}
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="Customers">
        <h1>{pageTitle}</h1>
        <div className="home-stats">
          <div className="stat-item">
            <span className="stat-number">{filteredResults.length}</span>
            <span className="stat-label">Total {pageTitle}</span>
          </div>
        </div>
      </div>
      <div className="search-and-actions">
        <div className="left-actions">
          <Link to="/CustomerMaint" className="new-ticket-btn">
            <span className="btn-icon">+</span>
            New {pageAttr}
          </Link>
        </div>
        <div className="search-container">
          <input
            type="text"
            placeholder="Search by name, email, phone, or address"
            value={searchQuery}
            onChange={handleSearch}
            className="search-input"
          />
        </div>
        <div className="right-actions">
          {/* Space for future actions like export/print */}
        </div>
      </div>

      {loading ? (
        <div className="loading-container">
          <img src={logo} style={{ width: '50px' }} alt="loading..." />
          <div className="loading-text">Loading customers...</div>
        </div>
      ) : (
        <>
          {currentResults.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">👥</div>
              <h3>No customers found</h3>
              <p>
                {searchQuery 
                  ? `No customers match your search for "${searchQuery}"`
                  : "No customers available. Create your first customer to get started!"
                }
              </p>
            </div>
          ) : (
            <>
              {/* Desktop Table View */}
              <div className="desktop-view">
                <div id="customerList" className="centered-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Phone</th>
                        <th>Address</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentResults.map((c) => (
                        <tr key={c.name}>
                          <td>
                            <Link to="/CustomerMaint" state={c}>
                              <strong>{c.name}</strong>
                            </Link>
                          </td>
                          <td>
                            <Link to="/CustomerMaint" state={c}>
                              {c.email || '-'}
                            </Link>
                          </td>
                          <td>
                            <Link to="/CustomerMaint" state={c}>
                              {c.phone || '-'}
                            </Link>
                          </td>
                          <td>
                            <Link to="/CustomerMaint" state={c}>
                              {c.address || '-'}
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Mobile Card View */}
              <div className="mobile-view">
                <div className="customer-cards">
                  {currentResults.map((c) => (
                    <div key={c.name} className="customer-card">
                      <Link to="/CustomerMaint" state={c} className="card-link">
                        <div className="card-header">
                          <span className="customer-name">{c.name}</span>
                          <span className="customer-icon">👤</span>
                        </div>
                        <div className="card-body">
                          {c.email && (
                            <div className="card-field">
                              <span className="field-label">Email:</span>
                              <span className="field-value">{c.email}</span>
                            </div>
                          )}
                          {c.phone && (
                            <div className="card-field">
                              <span className="field-label">Phone:</span>
                              <span className="field-value">{c.phone}</span>
                            </div>
                          )}
                          {c.address && (
                            <div className="card-field">
                              <span className="field-label">Address:</span>
                              <span className="field-value">{c.address}</span>
                            </div>
                          )}
                        </div>
                      </Link>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </>
      )}

      {!loading && renderPagination()}
    </>
  );
}

export default Customers;
